import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AlertTriangle, Globe, Info, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { SyncedSiteScopeDto } from '@/Domain/api/KnowledgeBase/dto/SyncedSite.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { cn } from '@/Domain/utils/cn';
import { UrlTree } from '@/UI/KnowledgeBase/SiteScrapeReview/UrlTree';
import {
  clearSelection,
  countSelected,
  mergeChildren,
  toggleSelection,
  UrlTreeNode,
} from '@/UI/KnowledgeBase/SiteScrapeReview/selection';

type BannerTone = 'info' | 'warning' | 'destructive';

type Banner = {
  tone: BannerTone;
  title: string;
  body?: string;
  chips?: string[];
};

const TONE_CLASS: Record<BannerTone, string> = {
  info: 'bg-info-muted text-info-foreground',
  warning: 'bg-warning-muted text-warning-foreground',
  destructive: 'bg-destructive-muted text-destructive-foreground',
};

const treeOf = (scope: SyncedSiteScopeDto | undefined): UrlTreeNode | null =>
  scope?.tree && 'path' in scope.tree ? (scope.tree as UrlTreeNode) : null;

type SyncedSiteScopeModalProps = {
  siteId: number;
  rootUrl: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRunCheck: (siteId: number) => void;
};

export const SyncedSiteScopeModal = ({
  siteId,
  rootUrl,
  open,
  onOpenChange,
  onRunCheck,
}: SyncedSiteScopeModalProps) => {
  const { t } = useTranslation('knowledge_base');
  const queryClient = useQueryClient();

  const [remapRequested, setRemapRequested] = useState(false);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState<string | null>(null);
  const selectionTouchedRef = useRef(false);
  const seededRef = useRef(false);

  const baseline = useQuery<SyncedSiteScopeDto>({
    queryKey: [QueryKey.knowledgeBase.syncedSites.scope, { siteId, remap: false }],
    enabled: open,
  });
  const remapQuery = useQuery<SyncedSiteScopeDto>({
    queryKey: [QueryKey.knowledgeBase.syncedSites.scope, { siteId, remap: true }],
    enabled: open && remapRequested,
    gcTime: 0,
  });

  const remapData = remapRequested ? remapQuery.data : undefined;
  const remapReady = remapData?.status === 'ready';
  // failed/throttled come back as HTTP 200 with a status field, so branch on the
  // payload, not isError. In both cases we fall back to the baseline tree.
  const displayedRemap = remapReady;
  const scope = displayedRemap ? remapData : baseline.data;
  const tree = treeOf(scope);
  const baselineTree = treeOf(baseline.data);

  const remapping = remapRequested && remapQuery.isFetching;
  const selectedCount = tree ? countSelected(tree, selection) : 0;
  // The server validates the selection against the stored baseline. After a
  // remap the user picks on the live tree, so a selection that covers only
  // newly revealed branches is non-empty live but empty against the baseline,
  // which the server would 400. Detect it here and explain it truthfully.
  const coversBaseline = baselineTree ? countSelected(baselineTree, selection) > 0 : true;

  // Reset everything when the modal closes so the next open starts clean.
  useEffect(() => {
    if (open) return;
    seededRef.current = false;
    selectionTouchedRef.current = false;
    setSelection(new Set());
    setRemapRequested(false);
    setSaveError(null);
  }, [open]);

  // Seed the checkboxes once, from the first ready tree. Seeding [tree.path]
  // when nothing is saved shows the site as fully in scope (which it is), but
  // we never persist that seed: Save stays disabled until the user toggles, so
  // an untouched close writes nothing. A later remap must NOT reseed, since
  // selection entries are path prefixes and a remap only adds nodes.
  useEffect(() => {
    if (!open || seededRef.current || !tree) return;
    const saved = scope?.selection ?? [];
    setSelection(new Set(saved.length ? saved : [tree.path]));
    seededRef.current = true;
  }, [open, tree, scope?.selection]);

  const onToggle = useCallback((path: string) => {
    selectionTouchedRef.current = true;
    setSaveError(null);
    setSelection((prev) => toggleSelection(prev, path));
  }, []);

  const onChildrenLoaded = useCallback(
    (path: string, batch: UrlTreeNode[]) => {
      queryClient.setQueryData<SyncedSiteScopeDto>(
        [QueryKey.knowledgeBase.syncedSites.scope, { siteId, remap: displayedRemap }],
        (old) =>
          old && old.tree && 'path' in old.tree
            ? { ...old, tree: mergeChildren(old.tree as UrlTreeNode, path, batch) }
            : old,
      );
    },
    [queryClient, siteId, displayedRemap],
  );

  const saveMutation = useMutation<
    SyncedSiteScopeDto,
    unknown,
    { siteId: number; selection: string[] }
  >({
    mutationKey: [QueryKey.knowledgeBase.syncedSites.scopeUpdate],
  });

  const handleRemap = useCallback(() => {
    setRemapRequested(true);
    void remapQuery.refetch();
  }, [remapQuery]);

  const handleReset = useCallback(() => {
    if (!tree) return;
    const saved = scope?.selection ?? [];
    setSelection(new Set(saved.length ? saved : [tree.path]));
    selectionTouchedRef.current = false;
    setSaveError(null);
  }, [tree, scope?.selection]);

  const handleDeselectAll = useCallback(() => {
    selectionTouchedRef.current = true;
    setSaveError(null);
    setSelection(clearSelection());
  }, []);

  const handleSave = useCallback(() => {
    setSaveError(null);
    saveMutation.mutate(
      { siteId, selection: Array.from(selection) },
      {
        onSuccess: () => {
          toast.success(t('synced_sites.scope.saved_toast'), {
            action: {
              label: t('synced_sites.scope.saved_toast_action'),
              onClick: () => onRunCheck(siteId),
            },
          });
          void queryClient.invalidateQueries({
            queryKey: [QueryKey.knowledgeBase.syncedSites.list],
          });
          onOpenChange(false);
        },
        onError: (error) => {
          const message =
            error instanceof AxiosError
              ? (error.response?.data?.errors?.body?.[0]?.message ?? error.response?.data?.detail)
              : null;
          setSaveError(message ?? t('synced_sites.scope.save_failed'));
        },
      },
    );
  }, [saveMutation, siteId, selection, t, onRunCheck, queryClient, onOpenChange]);

  const banner = useMemo<Banner | null>(() => {
    if (remapReady) {
      return {
        tone: 'info',
        title: t('synced_sites.scope.fresh.title'),
        body: t('synced_sites.scope.fresh.body', { count: remapData?.total_urls ?? 0 }),
      };
    }
    if (remapData?.status === 'failed') {
      return {
        tone: 'destructive',
        title: t('synced_sites.scope.remap_failed.title'),
        body: t('synced_sites.scope.remap_failed.body'),
      };
    }
    if (remapData?.status === 'throttled') {
      return { tone: 'warning', title: t('synced_sites.scope.remap_throttled') };
    }
    const base = baseline.data;
    if (!base || base.status !== 'ready') return null;
    if (base.excluded_prefixes.length || base.url_filter_regex) {
      const body = [
        t('synced_sites.scope.stale.body_branches'),
        base.url_filter_regex
          ? t('synced_sites.scope.stale.body_filter', { regex: base.url_filter_regex })
          : null,
      ]
        .filter(Boolean)
        .join(' ');
      return {
        tone: 'warning',
        title: t('synced_sites.scope.stale.title'),
        body,
        chips: base.excluded_prefixes,
      };
    }
    if (!base.mapped_at) {
      return {
        tone: 'info',
        title: t('synced_sites.scope.never_synced.title'),
        body: t('synced_sites.scope.never_synced.body'),
      };
    }
    return {
      tone: 'info',
      title: t('synced_sites.scope.stale.title_unfiltered'),
      body: t('synced_sites.scope.stale.body_unfiltered', {
        date: new Date(base.mapped_at).toLocaleDateString(),
      }),
    };
  }, [remapReady, remapData, baseline.data, t]);

  // First blocking validation wins; each disables Save with a truthful reason.
  const blockingMessage = useMemo<string | null>(() => {
    if (!tree) return null;
    if (saveError) return saveError;
    if (selectedCount === 0) return t('synced_sites.scope.excludes_everything');
    if (displayedRemap && !coversBaseline) return t('synced_sites.scope.only_new_sections');
    return null;
  }, [tree, saveError, selectedCount, displayedRemap, coversBaseline, t]);

  const canSave =
    !!tree &&
    selectionTouchedRef.current &&
    selectedCount > 0 &&
    coversBaseline &&
    !saveMutation.isPending &&
    !remapping;

  const status = scope?.status;
  const isEmpty = !displayedRemap && baseline.data?.status === 'empty' && !remapping;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !saveMutation.isPending) onOpenChange(false);
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
          <DialogPrimitive.Content
            onOpenAutoFocus={(event) => event.preventDefault()}
            className="pointer-events-auto flex h-[88vh] max-h-[720px] w-full max-w-2xl flex-col gap-4 rounded-2xl border border-border bg-card p-6 text-card-foreground shadow-2xl focus:outline-none"
            data-testid="synced-site-scope-modal"
          >
            <div className="flex flex-col gap-1">
              <DialogPrimitive.Title className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <span className="flex size-7 items-center justify-center rounded-lg bg-muted">
                  <Globe className="size-4 text-foreground" />
                </span>
                {t('synced_sites.scope.title')}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-sm text-muted-foreground">
                {t('synced_sites.scope.description')}
              </DialogPrimitive.Description>
              <p className="truncate text-xs text-foreground-subtle" title={rootUrl}>
                {rootUrl}
              </p>
            </div>

            {baseline.isLoading ? (
              <div
                className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground"
                data-testid="synced-site-scope-modal-loading"
              >
                <Spinner className="size-5" />
                <span className="text-sm">{t('synced_sites.scope.loading')}</span>
              </div>
            ) : baseline.isError ? (
              <div
                className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground"
                data-testid="synced-site-scope-modal-failed"
              >
                <AlertTriangle className="size-6 text-destructive" />
                <span className="max-w-sm text-center text-sm">
                  {t('synced_sites.scope.load_failed')}
                </span>
                <Button variant="secondary" size="sm" onClick={() => void baseline.refetch()}>
                  {t('synced_sites.scope.retry')}
                </Button>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-3">
                  {banner && (
                    <div
                      className={cn(
                        'flex flex-col gap-1.5 rounded-lg px-3 py-2.5 text-xs',
                        TONE_CLASS[banner.tone],
                      )}
                      data-testid="synced-site-scope-modal-banner"
                    >
                      <div className="flex items-start gap-2">
                        {banner.tone === 'info' ? (
                          <Info className="mt-0.5 size-3.5 shrink-0" />
                        ) : (
                          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                        )}
                        <div className="flex flex-col gap-1">
                          <span className="font-medium">{banner.title}</span>
                          {banner.body && <span className="opacity-90">{banner.body}</span>}
                          {banner.chips && banner.chips.length > 0 && (
                            <div className="mt-0.5 flex flex-col gap-1">
                              <span className="opacity-90">
                                {t('synced_sites.scope.stale.excluded_label')}
                              </span>
                              <div className="flex flex-wrap gap-1">
                                {banner.chips.map((prefix) => (
                                  <span
                                    key={prefix}
                                    className="rounded bg-background/40 px-1.5 py-0.5 font-mono"
                                    data-testid={`synced-site-scope-modal-banner-excluded-${prefix}`}
                                  >
                                    {prefix}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-3">
                    <span
                      className="text-sm font-medium text-foreground"
                      data-testid="synced-site-scope-modal-count"
                    >
                      {t('synced_sites.scope.in_scope', { count: selectedCount })}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={handleDeselectAll}
                        disabled={remapping || !tree}
                        data-testid="synced-site-scope-modal-deselect-all"
                      >
                        {t('synced_sites.scope.deselect_all')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={handleReset}
                        disabled={remapping || !selectionTouchedRef.current}
                        data-testid="synced-site-scope-modal-reset"
                      >
                        {t('synced_sites.scope.reset')}
                      </Button>
                    </div>
                  </div>
                </div>

                <div
                  className={cn(
                    'flex-1 overflow-y-auto rounded-lg border border-border p-2',
                    remapping && 'pointer-events-none opacity-50',
                  )}
                  data-testid="synced-site-scope-modal-tree"
                >
                  {isEmpty ? (
                    <div
                      className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground"
                      data-testid="synced-site-scope-modal-empty"
                    >
                      <span className="font-medium">{t('synced_sites.scope.empty.title')}</span>
                      <span className="text-xs">{t('synced_sites.scope.empty.body')}</span>
                    </div>
                  ) : tree ? (
                    <UrlTree
                      node={tree}
                      selection={selection}
                      onToggle={onToggle}
                      onChildrenLoaded={onChildrenLoaded}
                      childrenQueryKey={QueryKey.knowledgeBase.syncedSites.scopeChildren}
                      childrenParams={{ siteId, remap: displayedRemap }}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
                      {t('synced_sites.scope.empty_tree')}
                    </div>
                  )}
                </div>

                {blockingMessage && (
                  <p
                    className="text-xs text-destructive-foreground"
                    data-testid="synced-site-scope-modal-empty-warning"
                  >
                    {blockingMessage}
                  </p>
                )}

                <div className="flex items-center justify-between gap-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleRemap}
                    loading={remapping}
                    disabled={remapping}
                    data-testid="synced-site-scope-modal-remap-button"
                  >
                    <RefreshCw className="size-3.5" />
                    {status === 'ready' && displayedRemap
                      ? t('synced_sites.scope.remap_again')
                      : t('synced_sites.scope.remap')}
                  </Button>
                  <span className="min-w-0 flex-1 truncate text-xs text-foreground-subtle">
                    {t('synced_sites.scope.remap_hint')}
                  </span>
                </div>

                <p
                  className="text-xs text-muted-foreground"
                  data-testid="synced-site-scope-modal-future-note"
                >
                  {t('synced_sites.scope.future_only')}
                </p>

                <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onOpenChange(false)}
                    disabled={saveMutation.isPending}
                    data-testid="synced-site-scope-modal-cancel"
                  >
                    {t('synced_sites.scope.cancel')}
                  </Button>
                  <Button
                    variant="dark"
                    size="sm"
                    onClick={handleSave}
                    loading={saveMutation.isPending}
                    disabled={!canSave}
                    data-testid="synced-site-scope-modal-save"
                  >
                    {t('synced_sites.scope.save')}
                  </Button>
                </div>
              </>
            )}
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
